import { Box, Breadcrumbs, Divider, Link, Typography, Paper, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { ROUTES } from '../../../../constants/routes';
import { DoubleField } from '../../../support/components/DoubleField';
import { formatDate } from '../../../../utils/formatDate';
import { useDispatch, useSelector } from 'react-redux';
import { getBonusRecordById, getBonusRecordHistoryById } from '../../../../store/bonuses/actions';
import { requestGetBonusRecordXmlById } from '../../../../api/bonuses';
import { getBonusRecordColorStatus } from '../../constants/bonusRecordStatus';
import { CircularProgressCenter } from '../../../../components/CircularProgressCenter';
import { maskNumberPhone } from '../../../../utils/maskNumberPhone';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { PERMISSIONS } from '../../../../constants/variables';
import { NotFoundItems } from '../../../../components/NotFoundItems';
import { Modal } from '@material-ui/core';
import { ReactComponent as CloseModalIcon } from '../../../../assets/images/close-modal.svg';

export const BonusRecordDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const {
    bonusRecord,
    fetchingBonusRecord,
    bonusRecordError,
    bonusRecordHistoryFetching,
    bonusRecordHistory,
    bonusRecordHistoryError,
  } = useSelector((state) => state.bonuses);
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const [xmlModal, setXMLModal] = useState('');

  const isAllowedToRetrieveDeductionBonusTransactions = visibleBySectionPermission(permissions, PERMISSIONS.DEDUCTION.RetrieveDeductionBonusTransactions.value);
  const isAllowedToXML = visibleBySectionPermission(permissions, PERMISSIONS.DEDUCTION.DownloadBonusTransactionXML.value);

  const gridTemplateValues = '167px 137px 3.3fr 2.5fr';

  const getCardStatus = (status) => {
    return <Box color={getBonusRecordColorStatus(status)} data-testid={`status ${status}`}>{status}</Box>;
  }

  const getXML = async () => {
    try {
      setXMLModal(await requestGetBonusRecordXmlById(id));
    } catch (err) {
      setXMLModal(err.response?.status === 404 ? 'XML не найден' : 'Не удалось загрузить XML');
    }
  }

  useEffect(() => {
    if (isAllowedToRetrieveDeductionBonusTransactions) {
      dispatch(getBonusRecordById(id));
      dispatch(getBonusRecordHistoryById(id));
    } // eslint-disable-next-line
  }, []);

  if (
    isAllowedToRetrieveDeductionBonusTransactions && bonusRecord?.id !== id ||
    isAllowedToRetrieveDeductionBonusTransactions && !Object.values(bonusRecord).length ||
    fetchingBonusRecord ||
    bonusRecordHistoryFetching
  ) {
    return <CircularProgressCenter data-testid="CircularProgressCenter"/>;
  }

  return (
    <Box id="bonus-record-detail">
      <Breadcrumbs data-testid="Breadcrumbs">
        <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.BONUS_RECORD}>
          Учет бонусов
        </Link>
        <Box>Операция</Box>
      </Breadcrumbs>

      {
        isAllowedToRetrieveDeductionBonusTransactions ? (
          <>
            <Paper sx={{
              padding: '32px',
              marginTop: '16px',
              display: 'flex',
            }}
                   data-testid="PaperFirst"
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {
                  bonusRecordError && (
                    <Box color="error.main" fontSize="1rem" textAlign="center" data-testid="ErrorBlock">
                      {bonusRecordError}
                    </Box>
                  )
                }
                <Box width="25%" minWidth="230px">
                  <DoubleField
                    title="Дата создания"
                    description={formatDate(bonusRecord.created_at, false, false, true)}
                    data-testid="StartDate"
                  />
                  <DoubleField
                    title="Номер документа транзакции"
                    description={bonusRecord.document_number}
                    data-testid="Id"
                    mt={2}
                  />
                  <DoubleField
                    title="ID транзакции в МПШ"
                    description={bonusRecord.payment_transaction_id}
                    data-testid="TransactionId"
                    mt={2}
                  />
                </Box>
              </Box>

              <Divider width="1px" sx={{ margin: '0 24px', backgroundColor: '#DDE1E5' }} data-testid="Divider" />

              <Box width="75%">
                <Box display="flex" justifyContent='space-between'>
                  <Typography color="text.primary" sx={{ fontSize: '32px', fontWeight: 600 }}>{bonusRecord.document_number}</Typography>
                  <Typography color="text.primary" sx={{ fontSize: '20px' }}>{getCardStatus(bonusRecord.status)}</Typography>
                </Box>

                <Box>
                  <Box
                    sx={{
                      marginTop: '16px',
                      display: 'grid',
                      gridTemplateColumns: gridTemplateValues,
                    }}
                  >
                    <DoubleField
                      title="ФИО"
                      description={bonusRecord.name}
                      data-testid="Name"
                      descriptionWordsBreak={'break-word'}
                    />
                    <DoubleField
                      title="ИИН"
                      description={bonusRecord.customer_iin}
                      data-testid="IIN"
                    />
                    <DoubleField
                      title="Идентификационный номер клиента"
                      description={bonusRecord.customer_id}
                      data-testid="ClientId"
                    />
                    <DoubleField
                      title="Мобильный номер телефона"
                      description={maskNumberPhone(bonusRecord?.mobile_phone)}
                      data-testid="Mobile"
                    />
                  </Box>

                  <Box
                    sx={{
                      marginTop: '16px',
                      display: 'grid',
                      gridTemplateColumns: gridTemplateValues,
                    }}
                  >
                    <DoubleField
                      title="Лицевой счет"
                      description={bonusRecord.customer_account_id}
                      data-testid="Account"
                    />
                    <DoubleField
                      title="Глобальный ЛС"
                      description={bonusRecord.global_customer_account}
                      data-testid="AccountGlobal"
                    />
                    <DoubleField
                      title="Идентификатор региона ЛС"
                      description={bonusRecord.branch_customer_id}
                      data-testid="RegionId"
                    />
                    <DoubleField
                      title="Сумма"
                      description={bonusRecord.amount}
                      data-testid="Mobile"
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Paper sx={{ padding: '32px', marginTop: '16px' }} data-testid="PaperSecond">
              <Box display="flex" justifyContent="space-between">
              <Box sx={{ fontSize: '20px' }}>История событий</Box>
                {
                  (bonusRecord.xml_report && isAllowedToXML) ? (
                    <Typography
                      color="primary.main"
                      sx={{ cursor: 'pointer', ':hover': { textDecoration: 'underline' } }}
                      onClick={isAllowedToXML ? getXML : undefined}
                    >
                      Просмотреть XML
                    </Typography>
                  ) : null
                }
              </Box>
              <Box>
                {
                  bonusRecordHistoryFetching ?
                    (
                      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <CircularProgress />
                      </Box>
                    ) :
                    bonusRecordHistory.map((item) => {
                      return (
                        <Box key={item.document_number} display="grid" mt={3} sx={{gridTemplateColumns: '1.5fr 1fr 3fr 1fr', gap: '24px'}}>
                          <Box color="text.secondary">{formatDate(item.created_at, false, false, true)}</Box>
                          <Box sx={{ fontWeight: 500 }}
                               minWidth="235px">{item.user}
                          </Box>
                          <Box color="text.secondary">{item.transaction_status}</Box>
                        </Box>
                      );
                    })
                }
                {
                  bonusRecordHistoryError && (
                    <Box color="error.main" fontSize="1rem" textAlign="center" data-testid="ErrorBlock">
                      {bonusRecordHistoryError}
                    </Box>
                  )
                }
              </Box>
            </Paper>
          </>
        ) : (
          <NotFoundItems message="Вам запрещён доступ к просмотру деталей операции" />
        )
      }
      <Modal open={!!xmlModal} onClose={() => setXMLModal('')}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: 600,
          maxWidth: 800,
          minHeight: 452,
          bgcolor: 'background.paper',
          boxShadow: '0px 12px 32px rgba(42, 70, 94, 0.03)',
          borderRadius: '12px',
          outline: 'none'
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" p="24px">
            <Typography variant="h5" fontWeight={500}>
              { formatDate(bonusRecord.created_at, false, false, true) }
            </Typography>
            <CloseModalIcon cursor={'pointer'} onClick={() => setXMLModal('')} />
          </Box>
          <Divider />
          <Box
            sx={{
              padding: '20px',
              overflowY: 'scroll',
              maxHeight: 800
            }}
          >
            { xmlModal }
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
